"use client";

import { useState } from "react";
import { Frame, Slider, Verdict, Note } from "./parts";

const L = {
    fr: {
        title: "Ce que l'asynchrone fait gagner, et quand il ne fait rien gagner",
        tasks: "Nombre d'appels",
        share: "Part passée à attendre le réseau",
        seq: "En séquentiel",
        async: "En asynchrone",
        gain: "Gain",
        none: "aucun gain",
        waiting: "Les appels passent l'essentiel de leur temps à attendre une réponse. Les attentes se recouvrent, la durée totale se rapproche de celle d'un seul appel : c'est le terrain où l'asynchrone change tout.",
        computing: "Le temps part maintenant dans du calcul, et le calcul ne se recouvre pas : un seul fil l'exécute à la fois. L'asynchrone ne fait plus rien gagner, il ajoute seulement de la complexité au code.",
        middle: "Le gain existe mais il plafonne, parce que la part de calcul finit par dominer la durée totale. Passé ce point, c'est le calcul qu'il faut accélérer, pas l'attente.",
        note: "Le modèle suppose que les attentes se recouvrent parfaitement et que le calcul est sérialisé, ce qui est le comportement d'asyncio sur un seul fil. Le verrou global de l'interpréteur est la raison pour laquelle le calcul, lui, ne profite pas de l'asynchrone.",
        sec: "s",
    },
    en: {
        title: "What async buys you, and when it buys you nothing",
        tasks: "Number of calls",
        share: "Share spent waiting on the network",
        seq: "Sequential",
        async: "Asynchronous",
        gain: "Gain",
        none: "no gain",
        waiting: "The calls spend most of their time waiting for a reply. The waits overlap and total duration approaches that of a single call: this is the ground where async changes everything.",
        computing: "Time now goes into computation, and computation does not overlap: a single thread runs it at a time. Async buys nothing any more, it only adds complexity to the code.",
        middle: "The gain is real but it plateaus, because the computing share ends up dominating total duration. Past this point it is the computation that needs speeding up, not the waiting.",
        note: "The model assumes waits overlap perfectly and computation is serialised, which is how asyncio behaves on a single thread. The global interpreter lock is why computation, unlike waiting, gains nothing from async.",
        sec: "s",
    },
};

const DUREE = 1; // une seconde par appel, attente et calcul confondus

export default function AsyncTimeline({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [taches, setTaches] = useState(5);
    const [part, setPart] = useState(90);

    const attente = DUREE * (part / 100);
    const calcul = DUREE - attente;

    // En séquentiel tout s'additionne. En asynchrone les attentes se recouvrent,
    // le calcul reste sérialisé par le verrou global de l'interpréteur.
    const seq = taches * DUREE;
    const asyn = attente + taches * calcul;
    const gain = seq / asyn;

    const fmt = (v) => `${v.toLocaleString(lang === "fr" ? "fr-FR" : "en-US", { maximumFractionDigits: 1 })} ${t.sec}`;

    const barre = (valeur, couleur) => (
        <div className="h-8 rounded-lg bg-gray-100 dark:bg-white/[0.06] overflow-hidden">
            <div
                className={`h-full rounded-lg transition-all duration-300 ${couleur}`}
                style={{ width: `${(valeur / seq) * 100}%` }}
            />
        </div>
    );

    return (
        <Frame icon="clock-circle-linear" title={t.title}>
            <div className="space-y-4">
                <Slider label={t.tasks} value={taches} onChange={setTaches} min={1} max={20} />
                <Slider
                    label={t.share}
                    value={part}
                    onChange={setPart}
                    min={0}
                    max={100}
                    step={5}
                    display={`${part} %`}
                />
            </div>

            <div className="mt-6 space-y-4">
                <div>
                    <div className="flex justify-between items-baseline mb-1.5">
                        <span className="text-sm text-gray-600 dark:text-lightBlue">{t.seq}</span>
                        <span className="text-sm font-bold tabular-nums dark:text-white">{fmt(seq)}</span>
                    </div>
                    {barre(seq, "bg-gray-400 dark:bg-grayDark-500")}
                </div>
                <div>
                    <div className="flex justify-between items-baseline mb-1.5">
                        <span className="text-sm text-gray-600 dark:text-lightBlue">{t.async}</span>
                        <span className="text-sm font-bold tabular-nums dark:text-white">{fmt(asyn)}</span>
                    </div>
                    {barre(asyn, "bg-primary")}
                </div>
            </div>

            <div className="mt-4 text-sm text-gray-500 dark:text-lightBlue/80">
                {t.gain} :{" "}
                <span className="font-bold dark:text-white">
                    {gain < 1.05
                        ? t.none
                        : `${gain.toLocaleString(lang === "fr" ? "fr-FR" : "en-US", { maximumFractionDigits: 1 })} ×`}
                </span>
            </div>

            <Verdict tone={gain < 1.05 ? "bad" : gain > 3 ? "good" : "warn"}>
                {gain < 1.05 ? t.computing : gain > 3 ? t.waiting : t.middle}
            </Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
